/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

/**
 * Adapter Hierarchy Dashboard
 * Phase 2B: Configure adapter stacking and fallback chains per vertical
 */

import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout';
import adminApi from '../../services/adminApi';
import './AdapterHierarchy.css';

const AdapterHierarchy = () => {
  const [hierarchies, setHierarchies] = useState([]);
  const [adapters, setAdapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedVertical, setSelectedVertical] = useState(null);
  const [previewStack, setPreviewStack] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [hierarchyRes, adaptersRes, analyticsRes] = await Promise.all([
        adminApi.request('/admin/adapter-management/hierarchy'),
        adminApi.request('/admin/adapter-management/adapters'),
        adminApi.request('/admin/adapter-management/analytics/usage?days=7')
      ]);

      if (hierarchyRes.success) setHierarchies(hierarchyRes.data);
      if (adaptersRes.success) setAdapters(adaptersRes.data);
      if (analyticsRes.success) setAnalytics(analyticsRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load adapter configuration');
    } finally {
      setLoading(false);
    }
  };

  const updateHierarchy = useCallback(async (vertical, updates) => {
    try {
      const response = await adminApi.request(`/admin/adapter-management/hierarchy/${vertical}`, {
        method: 'PATCH',
        body: JSON.stringify(updates)
      });

      if (response.success) {
        setSuccess(`Updated ${vertical} configuration`);
        loadData();
      }
    } catch (err) {
      console.error('Failed to update hierarchy:', err);
      setError(`Failed to update ${vertical} configuration`);
    }
  }, []);

  const previewStackForVertical = async (vertical) => {
    try {
      const response = await adminApi.request(`/admin/adapter-management/hierarchy/${vertical}/preview`);
      if (response.success) {
        setPreviewStack(response.data);
        setSelectedVertical(vertical);
      }
    } catch (err) {
      console.error('Failed to preview stack:', err);
      setError('Failed to load stack preview');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'status-active',
      pending: 'status-pending',
      training: 'status-training',
      failed: 'status-failed',
      deprecated: 'status-deprecated'
    };
    return colors[status] || 'status-unknown';
  };

  const getVerticalStats = (vertical) => {
    const adapter = adapters.find(a => a.type === 'vertical' && a.vertical === vertical);
    return {
      dataCount: adapter?.training_data_count || 0,
      status: adapter?.status || 'pending'
    };
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="adapter-hierarchy-loading">
          <div className="spinner"></div>
          <p>Loading adapter configuration...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="adapter-hierarchy">
        {/* Header */}
        <div className="hierarchy-header">
          <div>
            <h1>Adapter Hierarchy</h1>
            <p>Configure how adapters stack for each vertical domain</p>
          </div>
          <button onClick={loadData} className="btn-refresh">
            Refresh
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div className="message message-error">
            {error}
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}
        {success && (
          <div className="message message-success">
            {success}
          </div>
        )}

        {/* Architecture Diagram */}
        <div className="architecture-diagram">
          <h2>Adapter Stack Architecture</h2>
          <div className="stack-flow">
            <div className="stack-layer layer-base">Base Model</div>
            <span className="arrow">→</span>
            <div className="stack-layer layer-foundation">Foundation</div>
            <span className="arrow">→</span>
            <div className="stack-layer layer-vertical">Vertical</div>
            <span className="arrow">→</span>
            <div className="stack-layer layer-tenant">Tenant</div>
            <span className="arrow">→</span>
            <div className="stack-layer layer-user">User</div>
            <span className="arrow">→</span>
            <div className="stack-layer layer-response">Response</div>
          </div>
          <p className="stack-note">
            Each layer adds domain-specific knowledge. If adapters fail, fallback to Claude → OpenAI → Groq
          </p>
        </div>

        {/* Quick Stats */}
        {analytics && (
          <div className="quick-stats">
            {analytics.summary.map(stat => (
              <div key={stat.source_type} className={`stat-card stat-${stat.source_type}`}>
                <div className="stat-icon">
                  {stat.source_type === 'adapter_stack' && '🧠'}
                  {stat.source_type === 'claude' && '🤖'}
                  {stat.source_type === 'openai' && '🔮'}
                  {stat.source_type === 'groq' && '⚡'}
                  {stat.source_type === 'error' && '⚠️'}
                </div>
                <div className="stat-info">
                  <span className="stat-value">{stat.total}</span>
                  <span className="stat-label">{stat.source_type.replace('_', ' ')}</span>
                </div>
                <div className="stat-avg">
                  {stat.avg_time_ms}ms avg
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Vertical Cards */}
        <div className="verticals-grid">
          {hierarchies.map(hierarchy => {
            const stats = getVerticalStats(hierarchy.vertical);
            return (
              <div key={hierarchy.vertical} className="vertical-card">
                {/* SEAL Candidate Banner */}
                {hierarchy.seal_status === 'candidate_available' && (
                  <div style={{
                    padding: '8px 16px', background: '#eff6ff', borderBottom: '1px solid #bfdbfe',
                    borderRadius: '8px 8px 0 0', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '10px'
                  }}>
                    <span>🤖 SEAL has a candidate {hierarchy.vertical} adapter ready for review</span>
                    <button
                      onClick={() => previewStackForVertical(hierarchy.vertical)}
                      style={{ background: '#dbeafe', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', color: '#1e40af' }}
                    >
                      View Details
                    </button>
                    <button
                      style={{ background: '#d1fae5', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', color: '#065f46' }}
                    >
                      Approve
                    </button>
                  </div>
                )}

                {/* Card Header */}
                <div className="card-header">
                  <div className="header-left">
                    <h3 className="vertical-name">{hierarchy.vertical}</h3>
                    <span className={`status-badge ${getStatusColor(stats.status)}`}>
                      {stats.status} {stats.dataCount > 0 && `(${stats.dataCount.toLocaleString()})`}
                    </span>
                    {/* SEAL Managed Badge */}
                    {hierarchy.seal_managed && (
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: '8px',
                        background: '#eff6ff', color: '#1e40af', fontSize: '11px', fontWeight: 500, marginLeft: '6px'
                      }}>
                        🤖 SEAL
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {/* Version Dropdown — ready for future multi-version support */}
                    <select
                      style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '12px', color: '#6b7280' }}
                      defaultValue="1.0"
                    >
                      <option value="1.0">v1.0</option>
                    </select>
                    <button
                      onClick={() => previewStackForVertical(hierarchy.vertical)}
                      className="btn-preview"
                    >
                      Preview Stack
                    </button>
                  </div>
                </div>

                {/* Stack Layers */}
                <div className="stack-layers">
                  {/* Foundation Layer */}
                  <div className="layer-row foundation">
                    <label className="layer-toggle">
                      <input
                        type="checkbox"
                        checked={hierarchy.foundation_enabled}
                        onChange={(e) => updateHierarchy(hierarchy.vertical, {
                          foundation_enabled: e.target.checked
                        })}
                      />
                      <span className="layer-name">Foundation</span>
                      {hierarchy.foundation_seal_managed && <span style={{ fontSize: '10px', color: '#3b82f6', marginLeft: '4px' }}>🤖</span>}
                      <span className="layer-status pending">Pending</span>
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="2"
                      value={hierarchy.foundation_weight}
                      onChange={(e) => updateHierarchy(hierarchy.vertical, {
                        foundation_weight: parseFloat(e.target.value)
                      })}
                      className="weight-input"
                      disabled={!hierarchy.foundation_enabled}
                    />
                  </div>

                  {/* Vertical Layer */}
                  <div className="layer-row vertical">
                    <label className="layer-toggle">
                      <input
                        type="checkbox"
                        checked={hierarchy.vertical_enabled}
                        onChange={(e) => updateHierarchy(hierarchy.vertical, {
                          vertical_enabled: e.target.checked
                        })}
                      />
                      <span className="layer-name capitalize">{hierarchy.vertical}</span>
                      <span className={`layer-status ${stats.status}`}>
                        {stats.status === 'active' ? `Active (${stats.dataCount})` : 'Pending'}
                      </span>
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="2"
                      value={hierarchy.vertical_weight}
                      onChange={(e) => updateHierarchy(hierarchy.vertical, {
                        vertical_weight: parseFloat(e.target.value)
                      })}
                      className="weight-input"
                      disabled={!hierarchy.vertical_enabled}
                    />
                  </div>

                  {/* Tenant Layer */}
                  <div className="layer-row tenant">
                    <label className="layer-toggle">
                      <input
                        type="checkbox"
                        checked={hierarchy.tenant_adapter_enabled}
                        onChange={(e) => updateHierarchy(hierarchy.vertical, {
                          tenant_adapter_enabled: e.target.checked
                        })}
                      />
                      <span className="layer-name">Tenant (B2B)</span>
                      <span className="layer-status future">Future</span>
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="2"
                      value={hierarchy.tenant_default_weight}
                      onChange={(e) => updateHierarchy(hierarchy.vertical, {
                        tenant_default_weight: parseFloat(e.target.value)
                      })}
                      className="weight-input"
                      disabled={!hierarchy.tenant_adapter_enabled}
                    />
                  </div>

                  {/* User Layer */}
                  <div className="layer-row user">
                    <label className="layer-toggle">
                      <input
                        type="checkbox"
                        checked={hierarchy.user_adapter_enabled}
                        onChange={(e) => updateHierarchy(hierarchy.vertical, {
                          user_adapter_enabled: e.target.checked
                        })}
                      />
                      <span className="layer-name">User Personal</span>
                      <span className="layer-status future">Future</span>
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="2"
                      value={hierarchy.user_default_weight}
                      onChange={(e) => updateHierarchy(hierarchy.vertical, {
                        user_default_weight: parseFloat(e.target.value)
                      })}
                      className="weight-input"
                      disabled={!hierarchy.user_adapter_enabled}
                    />
                  </div>
                </div>

                {/* Fallback & Transparency Config */}
                <div className="config-section">
                  <div className="config-group">
                    <span className="config-label">Fallbacks</span>
                    <div className="checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={hierarchy.fallback_to_claude}
                          onChange={(e) => updateHierarchy(hierarchy.vertical, {
                            fallback_to_claude: e.target.checked
                          })}
                        />
                        Claude
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={hierarchy.fallback_to_openai}
                          onChange={(e) => updateHierarchy(hierarchy.vertical, {
                            fallback_to_openai: e.target.checked
                          })}
                        />
                        OpenAI
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={hierarchy.fallback_to_groq}
                          onChange={(e) => updateHierarchy(hierarchy.vertical, {
                            fallback_to_groq: e.target.checked
                          })}
                        />
                        Groq
                      </label>
                    </div>
                  </div>

                  <div className="config-group">
                    <span className="config-label">User Transparency</span>
                    <div className="checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={hierarchy.show_response_source}
                          onChange={(e) => updateHierarchy(hierarchy.vertical, {
                            show_response_source: e.target.checked
                          })}
                        />
                        Show Icon
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={hierarchy.show_source_tooltip}
                          onChange={(e) => updateHierarchy(hierarchy.vertical, {
                            show_source_tooltip: e.target.checked
                          })}
                        />
                        Show Tooltip
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Stack Preview Modal */}
        {previewStack && (
          <div className="modal-overlay" onClick={() => setPreviewStack(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 className="capitalize">{selectedVertical} Stack Preview</h2>
                <button onClick={() => setPreviewStack(null)} className="modal-close">×</button>
              </div>

              <div className="modal-body">
                {/* Active Stack */}
                <div className="preview-section">
                  <h3 className="section-title success">Active Stack</h3>
                  {previewStack.stack.length > 0 ? (
                    <div className="stack-items">
                      {previewStack.stack.map((adapter, idx) => (
                        <div key={idx} className="stack-item active">
                          <span className="item-name">{adapter.name}</span>
                          <span className="item-weight">Weight: {adapter.weight}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-items">No active adapters - will use fallbacks</p>
                  )}
                </div>

                {/* Skipped */}
                {previewStack.skipped.length > 0 && (
                  <div className="preview-section">
                    <h3 className="section-title warning">Skipped</h3>
                    <div className="skipped-items">
                      {previewStack.skipped.map((skip, idx) => (
                        <div key={idx} className="skipped-item">
                          <span className="skip-layer">{skip.layer}:</span>
                          <span className="skip-reason">{skip.reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Fallbacks */}
                <div className="preview-section">
                  <h3 className="section-title info">Fallback Chain</h3>
                  <div className="fallback-chain">
                    {previewStack.fallbackConfig.claude && <span className="fallback-badge">Claude</span>}
                    {previewStack.fallbackConfig.openai && <span className="fallback-badge">OpenAI</span>}
                    {previewStack.fallbackConfig.groq && <span className="fallback-badge">Groq</span>}
                  </div>
                </div>

                {/* Transparency */}
                <div className="preview-section">
                  <h3 className="section-title">Transparency Settings</h3>
                  <div className="transparency-info">
                    <span>Show Source Icon: {previewStack.transparencyConfig.showSource ? 'Yes' : 'No'}</span>
                    <span>Show Tooltip: {previewStack.transparencyConfig.showTooltip ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button onClick={() => setPreviewStack(null)} className="btn-primary">
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdapterHierarchy;
